import React from 'react';
import { Divider, Field, Input, Select, Text } from '@fluentui/react-components';
import { useForm } from 'react-hook-form';
import { useAppActions, useAppState } from '../../context';
import { getValidationState } from './components/formValidation';
import { isPageReferenceActivity, PageReference } from '@gkn-ili/views/dist/OrderDocumentation';
import { AppEditDialogActions } from './components/dialogActions';
import {
  AppDialogBody,
  AppDialogContent,
  AppDialogHeader,
  AppDialogSurface,
  DialogOptions,
} from './components/baseComponents';
import { useLargeModalStyles as useLargeModalStyles } from './components/commonStyles';
import { MockOrderService } from '@gkn-ili/views/dist/mocks/order-service';

type FormState = { data: Parameters<MockOrderService['createActivity']>[1]; previousActivity: string };

export default function AppCreateActivityModal(props: {
  initialPreviousReference: PageReference;
  dialogOptions: DialogOptions;
}): JSX.Element {
  const styles = useLargeModalStyles();
  const appState = useAppState();
  const appActions = useAppActions();
  const routing = appState.orderView.routing[props.initialPreviousReference.routingItemKey];
  const step = routing.steps[props.initialPreviousReference.stepKey];
  const activityGroupKey =
    'activityGroupKey' in props.initialPreviousReference
      ? props.initialPreviousReference.activityGroupKey
      : Object.keys(step)[0];
  const activityGroup = step.activityGroups[activityGroupKey];

  const formHandlers = useForm<FormState>({
    defaultValues: {
      data: { name: 'New activity', description: '' },
      previousActivity: isPageReferenceActivity(props.initialPreviousReference)
        ? props.initialPreviousReference.activityKey
        : 'none',
    },
  });
  const onSubmit = formHandlers.handleSubmit(async (data) => {
    await appActions.editOrder.createActivity(
      { ...props.initialPreviousReference, activityGroupKey },
      data.data,
      data.previousActivity,
    );
    props.dialogOptions.onCloseDialog();
  });

  return (
    <AppDialogSurface dialogOptions={props.dialogOptions}>
      <form>
        <AppDialogBody dialogOptions={props.dialogOptions}>
          <AppDialogHeader dialogOptions={props.dialogOptions}>Create activity</AppDialogHeader>
          <AppDialogContent dialogOptions={props.dialogOptions}>
            <Text>Edit the activity details and click save to create the step.</Text>
            <Field label="Activity name" required {...getValidationState('data.name', formHandlers.formState.errors)}>
              <Input
                {...formHandlers.register('data.name', { required: true })}
                name="data.name"
                className={styles.field}
              />
            </Field>
            <Field
              label="Activity description"
              {...getValidationState('data.description', formHandlers.formState.errors)}
            >
              <Input {...formHandlers.register('data.description')} name="data.description" className={styles.field} />
            </Field>
            <Divider>Organization</Divider>
            <Field
              label="Comes after activity"
              required
              {...getValidationState('previousActivity', formHandlers.formState.errors)}
            >
              <Select {...formHandlers.register('previousActivity', { required: true })}>
                <option value="none">As first</option>
                {Object.entries(activityGroup.activities).map(([key, value]) => (
                  <option key={key} value={key}>
                    {value.name}
                  </option>
                ))}
              </Select>
            </Field>
          </AppDialogContent>
          <AppEditDialogActions
            onCancel={props.dialogOptions.onCloseDialog}
            submitDisabled={formHandlers.formState.isSubmitting}
            onSubmit={onSubmit}
          />
        </AppDialogBody>
      </form>
    </AppDialogSurface>
  );
}
